import { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Volume2 } from 'lucide-react';

import { useToolState } from '@/utils/persistence';

// Trilingual labels — 2026-05-17 fix. Compact + full variants both
// hardcoded FR ("Mot du jour", "voir un autre"). On /en/ and /es/ the
// footer leaked FR. Resolved via t below.
const LABELS = {
  fr: {
    eyebrow: 'Mot du jour',
    eyebrowFull: 'Le mot panaméen du jour',
    listen: 'Écouter la prononciation',
    another: 'voir un autre',
    cadence: 'Nouveau mot chaque jour',
    aria: 'Mot panaméen du jour',
  },
  en: {
    eyebrow: 'Word of the day',
    eyebrowFull: 'Panamanian word of the day',
    listen: 'Listen to the pronunciation',
    another: 'See another',
    cadence: 'A new word every day',
    aria: 'Panamanian word of the day',
  },
  es: {
    eyebrow: 'Palabra del día',
    eyebrowFull: 'La palabra panameña del día',
    listen: 'Escuchar la pronunciación',
    another: 'Ver otra',
    cadence: 'Nueva palabra cada día',
    aria: 'Palabra panameña del día',
  },
};

// Curated Panamanian Spanish vocabulary. Picks one per day deterministically
// based on the day of the year so it doesn't reshuffle on every refresh.
const PHRASES = [
  { es: '¡Qué xopá!', fr: 'Comment ça va !', en: 'How\'s it going!', tag: 'salutation' },
  { es: 'Chévere', fr: 'Cool / chouette', en: 'Cool', tag: 'adjectif' },
  { es: 'Fula', fr: 'L\'argent (familier)', en: 'Money (slang)', tag: 'argot' },
  { es: 'Vaina', fr: 'Truc, chose', en: 'Thing, stuff', tag: 'familier' },
  { es: 'Bro / cuñao', fr: 'Pote', en: 'Mate, dude', tag: 'familier' },
  { es: 'Ahuevao', fr: 'Stupide / ridicule', en: 'Foolish', tag: 'familier' },
  { es: 'Chuleta', fr: 'Mince ! / zut !', en: 'Damn!', tag: 'interjection' },
  { es: 'Pelao / pelaita', fr: 'Garçon / fille (jeune)', en: 'Kid / young person', tag: 'familier' },
  { es: 'Diablo rojo', fr: 'Bus collectif coloré', en: 'Colorful collective bus', tag: 'culture' },
  { es: 'Tuanis', fr: 'Très bien, parfait', en: 'Great, all good', tag: 'adjectif' },
  { es: 'Mola', fr: 'Tissu artisanal kuna', en: 'Kuna handmade fabric', tag: 'culture' },
  { es: 'Carimañola', fr: 'Beignet de yuca farci', en: 'Yuca fritter', tag: 'gastronomie' },
  { es: 'Ropa vieja', fr: 'Plat de bœuf effiloché', en: 'Shredded beef stew', tag: 'gastronomie' },
  { es: 'Sancocho', fr: 'Soupe au poulet et légumes', en: 'Chicken & root-veg stew', tag: 'gastronomie' },
  { es: 'Que en paz descanse', fr: 'Qu\'il/elle repose en paix', en: 'May they rest in peace', tag: 'expression' },
  { es: 'Buco', fr: 'Beaucoup', en: 'A lot', tag: 'familier' },
  { es: 'Joder', fr: 'Embêter / déranger', en: 'To bother', tag: 'verbe' },
  { es: 'Ñame', fr: 'Quelqu\'un de lent / lourd', en: 'Slow / dense person', tag: 'argot' },
  { es: 'Tongo', fr: 'Policier (familier)', en: 'Cop (slang)', tag: 'argot' },
  { es: 'Yeyo', fr: 'Malaise, étourdissement', en: 'Faintness, dizziness', tag: 'familier' },
  { es: 'Que sopla', fr: 'C\'est vraiment cool', en: 'That\'s really cool', tag: 'familier' },
  { es: 'Está al pelo', fr: 'C\'est nickel', en: 'Spot on', tag: 'expression' },
  { es: 'Chombo', fr: 'Afro-panaméen (West Indian descent)', en: 'Afro-Panamanian (West Indian descent)', tag: 'culture' },
  { es: 'Hacer una vaca', fr: 'Faire une cagnotte', en: 'Pool money together', tag: 'expression' },
];

/** Day-of-year index for deterministic daily selection. */
function dayOfYear(d = new Date()) {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d - start + (start.getTimezoneOffset() - d.getTimezoneOffset()) * 60_000;
  return Math.floor(diff / 86_400_000);
}

/**
 * Editorial widget: today's Panamanian Spanish phrase. Same phrase for all
 * users on the same day (deterministic by day-of-year). Click on the speaker
 * icon for browser-native text-to-speech in es-PA voice.
 *
 * @param {object} props
 * @param {string} [props.className]
 *
 * @example
 *   <SpanishPhraseOfDay />
 *   <SpanishPhraseOfDay compact />  // single-line footer variant
 */
export default function SpanishPhraseOfDay({ className = '', compact = false }) {
  const reduce = useReducedMotion();
  const { i18n } = useTranslation();
  const lang = ['fr', 'en', 'es'].includes(i18n.language) ? i18n.language : 'fr';
  const labels = LABELS[lang];
  // Persist a "shuffle" override so users can tap to see other phrases.
  // Stored as { day, idx } and SCOPED TO THE CURRENT DAY: once the stored day
  // is past, the override is ignored and the deterministic daily word resumes.
  // (Bug "Le mot du jour ne change plus" — a bare persisted index froze the
  // word forever after a single tap on "voir un autre". Legacy bare-number
  // values lack `.day`, so they fall through to daily and self-heal.)
  const [override, setOverride] = useToolState('phraseShuffle', null);

  const idx = useMemo(() => {
    const today = dayOfYear();
    if (override && override.day === today) return override.idx % PHRASES.length;
    return today % PHRASES.length;
  }, [override]);

  const phrase = PHRASES[idx];

  const speak = () => {
    if (!('speechSynthesis' in window)) return;
    const utter = new SpeechSynthesisUtterance(phrase.es);
    utter.lang = 'es-PA';
    utter.rate = 0.95;
    speechSynthesis.cancel();
    speechSynthesis.speak(utter);
  };

  const shuffle = () => {
    setOverride((prev) => {
      const today = dayOfYear();
      // Advance from the current shown index (today's override if any, else
      // today's daily word). The override is re-stamped with today's day so it
      // only holds until tomorrow, when daily rotation takes over again.
      const base = prev && prev.day === today ? prev.idx : today;
      return { day: today, idx: (base + 1) % PHRASES.length };
    });
  };

  // Compact mode — single line, dark-friendly. Used in the Footer.
  if (compact) {
    return (
      <div
        className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-cream-50/85 ${className}`}
        aria-label={labels.aria}
      >
        <span className="v4-eyebrow text-cream-50/60">{labels.eyebrow}</span>
        <span className="font-serif text-base italic text-cream-50">« {phrase.es} »</span>
        <span className="text-cream-50/70">— {phrase[lang] ?? phrase.fr}</span>
        <span className="rounded-full bg-cream-50/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-eyebrow text-cream-50/70">
          {phrase.tag}
        </span>
        <button
          type="button"
          onClick={speak}
          aria-label={labels.listen}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-cream-50/10 text-cream-50/80 hover:bg-bordeaux-600 hover:text-cream-50 transition-colors"
        >
          <Volume2 size={12} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={shuffle}
          className="text-xs text-cream-50/60 underline-offset-2 hover:text-cream-50 hover:underline"
        >
          {labels.another}
        </button>
      </div>
    );
  }

  return (
    <motion.section
      initial={reduce ? false : { opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.4 }}
      className={`group relative overflow-hidden rounded-card border border-navy-900/10 bg-gradient-to-br from-cream-50 to-cream-100 p-6 sm:p-7 dark:border-white/10 dark:from-zinc-900 dark:to-zinc-900 ${className}`}
      aria-labelledby="phrase-title"
    >
      <p id="phrase-title" className="v4-eyebrow mb-3 text-bordeaux-700 dark:text-bordeaux-400">
        {labels.eyebrowFull}
      </p>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-serif text-3xl font-bold italic text-navy-900 sm:text-4xl dark:text-white">
            « {phrase.es} »
          </p>
          {/* Two lines : translation in active locale (primary) + the other
              two locales below as secondary reference. The primary line
              picks fr/en/es per active lang ; the alternates row stays
              short and never includes the primary again. */}
          <p className="mt-2 text-base text-navy-900 dark:text-white">
            {phrase[lang] ?? phrase.fr}
          </p>
          <p className="mt-1 text-xs text-gray-700 dark:text-white/60">
            {['fr', 'en', 'es']
              .filter((l) => l !== lang)
              .map((l) => `${l.toUpperCase()}: ${phrase[l]}`)
              .join('  ·  ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-bordeaux-600/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-eyebrow text-bordeaux-700 dark:bg-bordeaux-400/20 dark:text-bordeaux-300">
            {phrase.tag}
          </span>
          <button
            type="button"
            onClick={speak}
            aria-label={labels.listen}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-navy-900/15 bg-cream-50 text-navy-900 hover:bg-bordeaux-600 hover:text-cream-50 hover:border-bordeaux-600 transition-colors dark:border-white/15 dark:bg-zinc-950 dark:text-white dark:hover:bg-bordeaux-500 dark:hover:border-bordeaux-500"
          >
            <Volume2 size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-gray-700 dark:text-white/60">
          {labels.cadence} ·{' '}
          <button onClick={shuffle} className="underline-offset-2 hover:underline hover:text-navy-900 dark:hover:text-white">
            {labels.another}
          </button>
        </p>
      </div>
    </motion.section>
  );
}
